import mapboxgl from 'mapbox-gl';

import Utils from '../../../../Assets/Scripts/Utils';

import { getAddressFromCoordinates } from './geocodingUtils';

import './CustomPopup.scss';

// Interrompe a animação da rota
export function stopRouteAnimation(map) {
  if (map && map.animationFrameId) {
    cancelAnimationFrame(map.animationFrameId);
    // eslint-disable-next-line no-param-reassign
    map.animationFrameId = null;
  }
}

// Desenha a rota animada
export function drawAnimatedRoute(map, coordinates) {
  if (!map) return;
  stopRouteAnimation(map);

  const sourceId = 'route';
  if (map.getSource(sourceId)) {
    if (map.getLayer('route-background')) map.removeLayer('route-background');
    if (map.getLayer('route-foreground')) map.removeLayer('route-foreground');
    map.removeSource(sourceId);
  }

  map.addSource(sourceId, {
    type: 'geojson',
    lineMetrics: true,
    data: { type: 'Feature', geometry: { type: 'LineString', coordinates } },
  });

  // Camada de fundo (cinza escuro, estática)
  map.addLayer({
    id: 'route-background',
    type: 'line',
    source: sourceId,
    paint: { 'line-color': '#4a4a4a', 'line-width': 5, 'line-opacity': 0.7 },
  });

  // Camada da frente (preta, animada para dar o efeito de fluxo)
  map.addLayer({
    id: 'route-foreground',
    type: 'line',
    source: sourceId,
    paint: { 'line-width': 4, 'line-color': 'black' },
  });

  let step = 0;
  function animate() {
    // A cada quadro, calcula o progresso da animação
    const progress = (step / 400) % 1;
    const start = progress;
    const end = (progress + 0.15) % 1; // O pulso terá 15% do tamanho da rota

    let gradient;
    if (start < end) {
      gradient = ['step', ['line-progress'], 'rgba(0,0,0,0)', start, 'black', end, 'rgba(0,0,0,0)'];
    } else {
      gradient = ['step', ['line-progress'], 'black', end, 'rgba(0,0,0,0)', start, 'black'];
    }

    map.setPaintProperty('route-foreground', 'line-gradient', gradient);

    step += 1;
    // eslint-disable-next-line no-param-reassign
    map.animationFrameId = requestAnimationFrame(animate);
  }
  animate();
}

// Retorna o SVG do ícone do operador com base no tipo
function getOperatorIcon(operatorType) {
  if (operatorType?.toLowerCase().includes('driver')) {
    // Ícone de Carro (CarOutlined)
    return '<svg viewBox="64 64 896 896" focusable="false" data-icon="car" width="1em" height="1em" fill="currentColor" aria-hidden="true"><path d="M834.3 403.7H732.9l-13.6-40.8c-7.9-23.8-30-40.8-55.2-40.8H359.9c-25.2 0-47.3 17-55.2 40.8l-13.6 40.8H189.7c-17.7 0-32 14.3-32 32v128c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8v-56h480v56c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8V435.7c0-17.7-14.3-32-32-32zM385.1 391.1h253.8l7.6-22.8a8.1 8.1 0 00-7.6-9.2H385.1a8.1 8.1 0 00-7.6 9.2l7.6 22.8zM260 635.7c-26.5 0-48 21.5-48 48s21.5 48 48 48 48-21.5 48-48-21.5-48-48-48zm504 0c-26.5 0-48 21.5-48 48s21.5 48 48 48 48-21.5 48-48-21.5-48-48-48zm-292.8-144H552.7v-40c0-4.4-3.6-8-8-8H479.3c-4.4 0-8 3.6-8 8v40z"></path></svg>';
  }
  // Ícone de Usuário (UserOutlined) para instrutores
  return '<svg viewBox="64 64 896 896" focusable="false" data-icon="user" width="1em" height="1em" fill="currentColor" aria-hidden="true"><path d="M858.5 763.6a374 374 0 00-80.6-119.5 375.63 375.63 0 00-119.5-80.6c-.4-.2-.8-.3-1.2-.5C719.5 518 760 444.7 760 362c0-137-111-248-248-248S264 225 264 362c0 82.7 40.5 156 102.8 201.1-.4.2-.8.4-1.2.5-44.8 18.9-85 46-119.5 80.6a375.63 375.63 0 00-80.6 119.5A371.7 371.7 0 00136 901.8a8 8 0 008 8.2h60c4.4 0 7.9-3.5 8-7.8 2-77.2 33-149.5 87.8-204.3 56.7-56.7 132-87.9 212.2-87.9s155.5 31.2 212.2 87.9C779 752.5 810 824.8 812 902.2c.1 4.3 3.6 7.8 8 7.8h60a8 8 0 008-8.2c-1-47.8-10.9-94.3-29.5-138.2zM512 534c-45.9 0-89.1-17.9-121.6-50.4S340 407.9 340 362c0-45.9 17.9-89.1 50.4-121.6S466.1 190 512 190s89.1 17.9 121.6 50.4S684 316.1 684 362c0 45.9-17.9 89.1-50.4 121.6S557.9 534 512 534z"></path></svg>';
}

// Inicializa uma instância do mapa Mapbox e a retorna
export function initializeMap(mapContainerRef, setIsMapLoaded) {
  try {
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12', // Tema claro (ruas)
      center: [-46.6333, -23.5505], // Ponto central padrão (São Paulo)
      zoom: 10,
      attributionControl: false,
    });

    map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    map.on('load', () => setIsMapLoaded(true));
    map.on('error', (e) => Utils.logError(e));

    return map;
  } catch (e) {
    Utils.logError(e);
    return null;
  }
}

// Cria um elemento de marcador personalizado
function createMarkerElement(className, innerHTML = '') {
  const el = document.createElement('div');
  el.className = className;
  el.innerHTML = innerHTML;
  return el;
}

// Cria popup customizado como HTML
function createCustomPopupHTML(title, address, type, isFixed) {
  const typeClass = `custom-popup custom-popup--${type} ${isFixed ? 'custom-popup--fixed' : ''}`;

  return `
    <div class="${typeClass}">
      <div class="custom-popup__content">
        ${address ? `<div class="custom-popup__address">${address}</div>` : ''}
      </div>
    </div>
  `;
}

// Cria e adiciona um marcador ao mapa com popup customizado
async function addMarkerToMap(map, { lng, lat, className, innerHTML, popupConfig }) {
  const el = createMarkerElement(className, innerHTML);
  const marker = new mapboxgl.Marker(el).setLngLat([lng, lat]);

  if (popupConfig) {
    // Busca o endereço se não foi fornecido
    const address = popupConfig.address || (await getAddressFromCoordinates(lng, lat));

    // Cria popup customizado
    const popup = new mapboxgl.Popup({
      offset: 15,
      closeButton: false,
      className: 'custom-mapbox-popup',
      closeOnClick: !popupConfig.isFixed, // Não fecha ao clicar se for fixo
      closeOnMove: !popupConfig.isFixed, // Não fecha ao mover se for fixo
    }).setHTML(
      createCustomPopupHTML(popupConfig.title, address, popupConfig.type, popupConfig.isFixed)
    );

    marker.setPopup(popup);
    marker.addTo(map);

    // Se é fixo, abre automaticamente e mantém aberto
    if (popupConfig.isFixed) {
      // Aguarda um pequeno delay para garantir que o marker foi adicionado ao DOM
      setTimeout(() => {
        if (marker.getPopup() && map.getContainer()) {
          marker.togglePopup();
        }
      }, 200);
    }

    return marker;
  }

  return marker.addTo(map);
}

// Desenha os pontos e a rota no mapa
export async function drawTrackingData(map, trackingData, markersRef, task, selectedOperator) {
  const pointsToBound = [];

  // Verifica se há dados de rastreamento para este operador
  if (!trackingData || trackingData.length === 0) {
    stopRouteAnimation(map);
    return pointsToBound; // Não gera nenhum marcador se não houver dados de rastreamento
  }

  const sortedData = [...trackingData].sort(
    (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
  );

  // 1. Adiciona o marcador de destino (sempre fixo)
  const destLat = parseFloat(task.latitude);
  const destLng = parseFloat(task.longitude);
  if (!Number.isNaN(destLat) && !Number.isNaN(destLng)) {
    const marker = await addMarkerToMap(map, {
      lng: destLng,
      lat: destLat,
      className: 'destination-marker',
      innerHTML: '<div class="inner-square"></div>',
      popupConfig: {
        title: 'Destino',
        type: 'destination',
        isFixed: true,
      },
    });
    markersRef.current.push(marker);
    pointsToBound.push([destLng, destLat]);
  }

  // 2. Desenha a rota
  if (sortedData.length > 1) {
    drawAnimatedRoute(
      map,
      sortedData.map((p) => [p.longitude, p.latitude])
    );
  } else {
    stopRouteAnimation(map);
  }

  // 3. Adiciona o marcador de início (sempre fixo)
  const startPoint = sortedData[0];
  const startMarker = await addMarkerToMap(map, {
    lng: startPoint.longitude,
    lat: startPoint.latitude,
    className: 'start-marker',
    innerHTML: '<div class="inner-dot"></div>',
    popupConfig: {
      title: 'Início',
      type: 'start',
      isFixed: true,
    },
  });
  markersRef.current.push(startMarker);
  pointsToBound.push([startPoint.longitude, startPoint.latitude]);

  // 4. Adiciona marcador de última posição (clicável se a rota não terminou)
  if (!selectedOperator?.trackingEnd) {
    const lastPoint = sortedData[sortedData.length - 1];
    if (
      lastPoint.latitude !== startPoint.latitude ||
      lastPoint.longitude !== startPoint.longitude
    ) {
      const lastMarker = await addMarkerToMap(map, {
        lng: lastPoint.longitude,
        lat: lastPoint.latitude,
        className: 'operator-marker',
        innerHTML: getOperatorIcon(selectedOperator?.type),
        popupConfig: {
          title: 'Última Posição',
          type: 'operator',
          isFixed: false, // Clicável
        },
      });

      markersRef.current.push(lastMarker);
      pointsToBound.push([lastPoint.longitude, lastPoint.latitude]);
    }
  }

  return pointsToBound;
}

// Ajusta o zoom do mapa para incluir todos os pontos da rota e marcadores
export function fitMapToData(map, trackingData, task) {
  if (!map) return;

  const allPoints = (trackingData || []).map((p) => [p.longitude, p.latitude]);

  const destLat = parseFloat(task?.latitude);
  const destLng = parseFloat(task?.longitude);
  if (!Number.isNaN(destLat) && !Number.isNaN(destLng)) {
    allPoints.push([destLng, destLat]);
  }

  if (allPoints.length === 0) return;

  if (allPoints.length === 1) {
    map.flyTo({
      center: allPoints[0],
      zoom: 15,
      duration: 1000,
    });
  } else {
    const bounds = new mapboxgl.LngLatBounds();
    allPoints.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds, { padding: 30, duration: 1000 });
  }
}

export async function updateSelectedLocationMarker(map, selectedLocation, markerRef) {
  if (markerRef.current) {
    markerRef.current.remove();
    // eslint-disable-next-line no-param-reassign
    markerRef.current = null;
  }

  if (!map || !selectedLocation) return;

  const el = document.createElement('div');
  el.className = 'selected-location-marker';
  el.innerHTML = '<div class="inner-dot"></div>';

  const address = await getAddressFromCoordinates(selectedLocation.lng, selectedLocation.lat);

  const popup = new mapboxgl.Popup({
    offset: 15,
    closeButton: false,
    className: 'custom-mapbox-popup',
    closeOnClick: false,
  }).setHTML(`
    <div class="custom-popup custom-popup--selected">
      <div class="custom-popup__content">
        <div class="custom-popup__address">${address}</div>
      </div>
    </div>
  `);

  // eslint-disable-next-line no-param-reassign
  markerRef.current = new mapboxgl.Marker(el)
    .setLngLat([selectedLocation.lng, selectedLocation.lat])
    .setPopup(popup)
    .addTo(map);

  map.flyTo({
    center: [selectedLocation.lng, selectedLocation.lat],
    zoom: 17,
    duration: 1200,
  });
}

export function clearMapData(map, markersRef) {
  if (!map) return;
  stopRouteAnimation(map);
  markersRef.current.forEach((m) => m.remove());
  // eslint-disable-next-line no-param-reassign
  markersRef.current = [];

  if (map.getSource('route')) {
    if (map.getLayer('route-background')) map.removeLayer('route-background');
    if (map.getLayer('route-foreground')) map.removeLayer('route-foreground');
    map.removeSource('route');
  }
}

// ====== ETA util ======
const etaCache = new Map();
const ETA_TTL_MS = 60000; // 1 min

export async function getRouteEta(origin, dest) {
  if (!origin || !dest) return null;

  const key = `${origin.lng.toFixed(5)},${origin.lat.toFixed(5)}|${dest.lng.toFixed(
    5
  )},${dest.lat.toFixed(5)}`;
  const cached = etaCache.get(key);
  const now = Date.now();
  if (cached && now - cached.ts < ETA_TTL_MS) return cached.value;

  try {
    const url =
      `https://api.mapbox.com/directions/v5/mapbox/driving-traffic/` +
      `${origin.lng},${origin.lat};${dest.lng},${dest.lat}` +
      `?overview=false&annotations=duration&access_token=${mapboxgl.accessToken}`;

    const res = await fetch(url);
    if (!res.ok) throw new Error('Directions request failed');
    const data = await res.json();
    const seconds = data?.routes?.[0]?.duration ?? null;
    etaCache.set(key, { value: seconds, ts: now });
    return seconds;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('ETA fetch error', e);
    return null;
  }
}

export function formatEta(seconds) {
  if (!seconds || Number.isNaN(seconds)) return null;
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${m.toString().padStart(2, '0')}m`;
}

// Retorna ETA formatado ou null (considera rota em progresso)
export async function fetchFormattedEta(selectedOperator, trackingData, task) {
  const inProgress = selectedOperator?.trackingStart && !selectedOperator?.trackingEnd;
  if (!inProgress || !trackingData?.length) return null;

  const destLat = parseFloat(task?.latitude);
  const destLng = parseFloat(task?.longitude);
  if (Number.isNaN(destLat) || Number.isNaN(destLng)) return null;

  const lastPoint = trackingData[trackingData.length - 1];
  if (!lastPoint) return null;

  const seconds = await getRouteEta(
    { lat: lastPoint.latitude, lng: lastPoint.longitude },
    { lat: destLat, lng: destLng }
  );
  return formatEta(seconds);
}
